import { configureUiLocale } from "@cafe/ui";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
import { en } from "./en";
import { ky } from "./ky";
import { ru } from "./ru";
import { uz } from "./uz";

export const LOCALES = [
  { value: "ru", label: "Русский" },
  { value: "ky", label: "Кыргызча" },
  { value: "uz", label: "Oʻzbekcha" },
  { value: "en", label: "English" },
] as const;
export type Locale = (typeof LOCALES)[number]["value"];

/** Формат чисел/дат по языку интерфейса. */
const NUMBER_LOCALE: Record<Locale, string> = { ru: "ru-RU", ky: "ky-KG", uz: "uz-Latn-UZ", en: "en-US" };
/** Встроенные сообщения Zod (для правил без своего текста); кыргызского в zod нет — используем русский. */
const ZOD_LOCALE: Record<Locale, () => Parameters<typeof z.config>[0]> = { ru: z.locales.ru, ky: z.locales.ru, uz: z.locales.uz, en: z.locales.en };

const KEY = "cafe.locale";
const isLocale = (v: unknown): v is Locale => LOCALES.some((l) => l.value === v);
const saved = (() => {
  try {
    const v = localStorage.getItem(KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
})();

/** Тексты UI-kit (@cafe/ui), формат денег и сообщения Zod — вслед за языком приложения. */
function syncLocale(lng: string) {
  const l: Locale = isLocale(lng) ? lng : "ru";
  const t = i18n.t;
  document.documentElement.lang = l;
  z.config(ZOD_LOCALE[l]());
  configureUiLocale({
    numberLocale: NUMBER_LOCALE[l],
    currencySymbols: { KGS: t("currencySymbols.KGS"), KZT: t("currencySymbols.KZT"), UZS: t("currencySymbols.UZS"), RUB: t("currencySymbols.RUB"), TJS: t("currencySymbols.TJS") },
    text: {
      ok: t("ui.ok"), confirm: t("ui.confirm"), cancel: t("common.cancel"), delete: t("common.delete"), actionFailed: t("ui.actionFailed"),
      typeToConfirmBefore: t("ui.typeToConfirmBefore"), typeToConfirmAfter: t("ui.typeToConfirmAfter"), dialog: t("ui.dialog"), close: t("ui.close"),
      clear: t("ui.clear"), showPassword: t("ui.showPassword"), hidePassword: t("ui.hidePassword"), search: t("common.search"), select: t("ui.select"),
      nothingFound: t("common.nothingFound"), loadError: t("ui.loadError"), create: (q) => t("ui.create", { q }), increase: t("ui.increase"),
      decrease: t("ui.decrease"), loading: t("ui.loading"), actions: t("common.actions"), pages: t("ui.pages"),
      pageRange: (from, to, total) => t("ui.pageRange", { from, to, total }), prevPage: t("ui.prevPage"), nextPage: t("ui.nextPage"),
      selectAll: t("ui.selectAll"), selectRow: t("ui.selectRow"), selectedCount: (count) => t("ui.selectedCount", { count }), clearSelection: t("ui.clearSelection"),
      grams: t("ui.grams"), kilograms: t("ui.kilograms"), weight: t("ui.weight"), perKg: t("ui.perKg"),
    },
  });
}

i18n.on("languageChanged", syncLocale);
void i18n.use(initReactI18next).init({
  resources: { ru: { translation: ru }, ky: { translation: ky }, uz: { translation: uz }, en: { translation: en } },
  lng: saved ?? "ru",
  fallbackLng: "ru",
  interpolation: { escapeValue: false },
  // Ресурсы встроены — инициализация синхронно, до первого рендера
  initAsync: false,
});

export function setLocale(l: Locale) {
  void i18n.changeLanguage(l);
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* приватный режим */
  }
}

export default i18n;
